import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { Animated, Platform, StyleSheet } from 'react-native'
import RNBootSplash from 'react-native-bootsplash'
import FastImage from 'react-native-fast-image'

const FADE_OUT_DURATION = 500
const IMAGE_PAINT_TIMEOUT = 3000

export interface LaunchLoadingScreenProps {
  appReady: boolean
  ready: boolean
  imageUri?: string | null
  onFadeOutComplete: () => void
}

export const LaunchLoadingScreen: FunctionComponent<LaunchLoadingScreenProps> = ({ appReady, ready, imageUri, onFadeOutComplete }) => {
  const opacity = useRef(new Animated.Value(1)).current
  const [imagePainted, setImagePainted] = useState(false)

  useEffect(() => {
    if (!imageUri) {
      return
    }
    const timer = setTimeout(() => setImagePainted(true), IMAGE_PAINT_TIMEOUT)
    return () => clearTimeout(timer)
  }, [imageUri])

  useEffect(() => {
    if (Platform.OS === 'web' || (imageUri ? !imagePainted : !appReady)) {
      return
    }
    RNBootSplash.hide({ fade: true })
  }, [imageUri, imagePainted, appReady])

  useEffect(() => {
    if (!ready) {
      return
    }
    if (!imageUri) {
      onFadeOutComplete()
      return
    }
    Animated.timing(opacity, {
      toValue: 0,
      duration: FADE_OUT_DURATION,
      useNativeDriver: true,
    }).start(onFadeOutComplete)
  }, [ready, imageUri])

  if (!imageUri) {
    return null
  }

  return (
    <Animated.View style={[StyleSheet.absoluteFill, { opacity }]} pointerEvents="none">
      <FastImage
        source={{ uri: imageUri, cache: FastImage.cacheControl.immutable }}
        style={StyleSheet.absoluteFill}
        resizeMode={FastImage.resizeMode.cover}
        onLoad={() => setImagePainted(true)}
        onError={() => setImagePainted(true)}
      />
    </Animated.View>
  )
}
